/**
 * The presets browser's right side: where you are (source / folders), search,
 * the folder's sub-folders as tiles and its preset cards, and the ghost that follows the
 * pointer while a card is dragged (onto the graph to add it, onto a folder to move or copy it).
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import type { Preset } from "@protocol/Preset";
import { useApp } from "../../app/context";
import type { SourceId } from "../../state/presets";
import { createArming } from "../../shared/arming";
import { emptyDraft } from "../../shared/drafts";
import { childFolders, countWithin, crumbs, folderName, presetsShown } from "../../shared/presetTree";
import { toast } from "../../shared/toast";
import { KindShape, useNodeColor } from "../../shared/ui";
import type { Folders } from "./folders";
import { PresetCard } from "./PresetCard";
import { addPreset, dragPreset } from "./placement";
import type { Spot } from "../../shared/spot";

/** What the gallery shows besides the presets. */
export interface PresetGalleryProps {
  /** Folder tools shared with the tree. */
  folders: Folders;
  /** Buttons at the end of the header (dock or undock). */
  tools?: JSX.Element;
  /** A preset was placed on the graph (the drawer then closes). */
  onPlaced?: () => void;
}

/** The current folder's tiles and cards with its search. */
export function PresetGallery(props: PresetGalleryProps): JSX.Element {
  const { presets, view, graph, scene, menu, kinds } = useApp();
  const color = useNodeColor();
  const [query, setQuery] = createSignal("");
  const arming = createArming();
  const [ghost, setGhost] = createSignal<{ preset: Preset; x: number; y: number } | null>(null);

  const source = () => presets.source(presets.place().source);
  const folder = () => presets.place().folder;
  const library = () => source()?.library ?? { folders: [], presets: [] };
  const shown = createMemo(() => presetsShown(library(), folder(), { query: query().toLowerCase() }));
  const subfolders = createMemo(() => (query() ? [] : childFolders(library(), folder())));

  /** Adds the preset's node (next to the selection, or at `spot`) and selects it. */
  const add = async (preset: Preset, spot?: Spot) => {
    await addPreset({ presets, view, scene }, preset, spot);
    props.onPlaced?.();
  };
  /** A card dropped on a folder: moved there (same source) or copied (another source). */
  const toFolder = (preset: Preset, from: SourceId, to: string, path: string) => {
    if (to === from) {
      if (preset.folder === path) return;
      void presets.edit(from, { edit: "move", id: preset.id, folder: path }).then((ok) => ok && toast(`Moved ${preset.label} to ${path ? folderName(path) : presets.source(from)?.label}.`));
    } else void presets.copy(preset, to as SourceId, path);
  };
  /** Starts dragging a card. */
  const startDrag = (e: PointerEvent, preset: Preset) => {
    const from = presets.place().source;
    dragPreset(e, preset, { ghost: setGhost, drop: (spot) => void add(preset, spot), toFolder: (to, path) => toFolder(preset, from, to, path) });
  };
  /** Deletes a preset (second click). */
  const remove = (preset: Preset) => {
    const from = presets.place().source;
    arming.confirm(preset.id, () => void presets.edit(from, { edit: "remove", id: preset.id }).then((ok) => ok && toast("Preset deleted.")));
  };

  return (
    <div class="pgallery">
      <div class="dhead">
        <span class="pcrumbs" id="preset-crumbs">
          <button type="button" class="pcrumb" onClick={() => presets.go(presets.place().source, "")}>
            {source()?.label}
          </button>
          <For each={crumbs(folder())}>
            {(c) => (
              <>
                <span class="pcrumb-sep">/</span>
                <button type="button" class="pcrumb" onClick={() => presets.go(presets.place().source, c.path)}>
                  {c.name}
                </button>
              </>
            )}
          </For>
        </span>
        <input class="search" type="search" placeholder="Search presets" aria-label="Filter presets" onInput={(e) => setQuery(e.currentTarget.value.trim())} />
        <span class="sp" />
        <Show when={!source()?.readOnly}>
          <button type="button" class="mini" id="new-preset-folder" onClick={() => props.folders.startNew(presets.place().source, folder())}>
            New folder
          </button>
        </Show>
        <button type="button" class="btn primary" id="new-preset" onClick={() => presets.setDraft(emptyDraft())}>
          New preset
        </button>
        {props.tools}
      </div>
      <div class="dbody" onContextMenu={(e) => e.target === e.currentTarget && menu.open(e, props.folders.menu(presets.place().source, folder()))}>
        <Show when={source()?.readOnly}>
          <p class="pnote" id="preset-read-only">
            {source()?.why}
          </p>
        </Show>
        <div class="pgrid">
          <For each={subfolders()}>
            {(path) => (
              <button
                type="button"
                class="pfolder"
                data-folder-tile={path}
                data-drop-source={source()?.readOnly ? undefined : presets.place().source}
                data-drop-folder={path}
                onClick={() => presets.go(presets.place().source, path)}
                onContextMenu={(e) => menu.open(e, props.folders.menu(presets.place().source, path))}
              >
                <span class="pfolder-icon" aria-hidden="true" />
                <span class="pname">{folderName(path)}</span>
                <span class="pmeta">{countWithin(library(), path)} presets</span>
              </button>
            )}
          </For>
          <For each={shown()}>
            {(p) => <PresetCard preset={p} readOnly={!!source()?.readOnly} armed={arming.armed() === p.id} onAdd={() => void add(p)} onDragStart={(e) => startDrag(e, p)} onDelete={() => remove(p)} />}
          </For>
          <button type="button" class="pnewcard" onClick={() => presets.setDraft(emptyDraft())}>
            <span>+</span>New preset
          </button>
        </div>
        <Show when={!shown().length && !subfolders().length}>
          <p class="pempty">{query() ? "No preset matches here or below." : "This folder is empty. Drag a preset here, or make a new one."}</p>
        </Show>
      </div>
      <Show when={ghost()}>
        {(g) => (
          <Portal>
            <div class="ghost" style={{ transform: `translate(${g().x + 12}px, ${g().y + 12}px)` }}>
              <span style={{ color: color(g().preset), display: "inline-flex" }}>
                <KindShape shape={kinds.ofNode(g().preset).shape} />
              </span>
              <span>{g().preset.name}</span>
            </div>
          </Portal>
        )}
      </Show>
      <Show when={!graph.state.loaded}>
        <p class="pempty">Waiting for the core…</p>
      </Show>
    </div>
  );
}
